import { ComponentChild } from 'preact'
import { Fragment } from 'preact/jsx-runtime'

import type { SharedProps } from '@bonniernews/dn-design-system-web/assets/types/shared-props.ts'

import { formatClassString } from '@bonniernews/dn-design-system-web/helpers/formatClassString.ts'

import { BylineList } from '@bonniernews/dn-design-system-web/components/byline-list/byline-list.tsx'
import { Modal } from '@bonniernews/dn-design-system-web/components/modal/modal.tsx'

/* This mainly corresponds to byline data from our APIs */
export interface BylineData {
  name: string
  authorId: string
  bylineImage?: ComponentChild
  authorPageUrl?: string
  followable: boolean
  followed?: boolean
  followButtonAttributes?: Record<string, string>
  border?: boolean
  role: string
}

interface BylineProps extends SharedProps {
  largeByline?: boolean
  bylineTitle?: string
  bylines: BylineData[]
  hideBylineImages?: boolean
  hideBorder?: boolean
  renderBylineModal?: boolean
  showNameAsLink?: boolean
}

/**
 * - GitHub: [BonnierNews/dn-design-system/web/src/components/byline](https://github.com/BonnierNews/dn-design-system/tree/main/web/src/components/byline)
 * - Storybook: [Byline](https://designsystem.dn.se/?path=/docs/article-byline--docs)
 *
 * The component will not include styling by itself. Make sure to include the right styles for the component. See example below:
 * `@use '@bonniernews/dn-design-system-web/components/byline/byline.scss'`
 *
 * ### Javascript
 *
 * ```javascript
 * import { initModalByline } from '@bonniernews/dn-design-system-web/components/byline/byline-interaction.js'
 * const articleElement = document.querySelector('.some-article-class');
 * initModalByline(articleElement);
 * ```
 */

export const Byline = ({
  bylines,
  bylineTitle,
  largeByline = false,
  hideBylineImages = false,
  hideBorder = false,
  renderBylineModal = false,
  showNameAsLink = false,
  classNames,
  attributes,
}: BylineProps) => {
  const componentClassName = 'ds-byline'
  const classes = formatClassString([
    componentClassName,
    largeByline ? `${componentClassName}--large` : '',
    !hideBylineImages ? `${componentClassName}--show-images` : '',
    hideBorder ? `${componentClassName}--no-border` : '',
    classNames,
  ])

  const shouldRenderModal = !!bylines.length && renderBylineModal && !showNameAsLink

  return (
    <>
      <div className={classes} {...attributes}>
        {bylines.length === 1 ? (
          <div className={`${componentClassName}__inner`}>
            {!hideBylineImages && (
              <div
                className={largeByline ? `${componentClassName}__portrait--large` : `${componentClassName}__portrait`}
              >
                {bylines[0].bylineImage}
              </div>
            )}
            <div
              className={`${componentClassName}__info ${!bylineTitle ? `${componentClassName}__info--no-title` : ''}`}
            >
              {bylineTitle && <span className={`${componentClassName}__role`}>{bylineTitle}</span>}
              <a
                href={bylines[0].authorPageUrl || '#'}
                className={`${componentClassName}__name`}
                data-link-category='article author'
                data-id={bylines[0].authorPageUrl}
                data-index='1'
                data-label={bylines[0].name}
              >
                {bylines[0].name}
              </a>
            </div>
          </div>
        ) : (
          <div className={`${componentClassName}__inner ${componentClassName}__inner--multiple`}>
            {!hideBylineImages &&
              bylines.map((byline, index) => (
                <div className={`${componentClassName}__portrait`} key={`byline-portrait-${index}`}>
                  {byline.bylineImage}
                </div>
              ))}
            <div className={`${componentClassName}__info`}>
              {bylineTitle && <span className={`${componentClassName}__role`}>{bylineTitle}</span>}
              {bylines.map((byline, index) => {
                const { authorPageUrl } = byline
                const isLast = index === bylines.length - 1
                return (
                  <Fragment key={`byline-name-${index}`}>
                    {isLast && <span className={`${componentClassName}__and`}>och </span>}
                    <AuthorName
                      renderLink={showNameAsLink && !!authorPageUrl}
                      byline={byline}
                      componentClassName={componentClassName}
                      index={index + 1}
                      showComma={index < bylines.length - 2}
                    />{' '}
                  </Fragment>
                )
              })}
            </div>
          </div>
        )}
      </div>

      {shouldRenderModal && (
        <Modal modalType='sheet' classNames='ds-modal--byline'>
          <BylineList bylines={bylines} />
        </Modal>
      )}
    </>
  )
}

interface AuthorNameProps {
  renderLink?: boolean
  byline: BylineData
  componentClassName: string
  index: number
  showComma: boolean
}

const AuthorName = ({ renderLink, byline, componentClassName, index, showComma }: AuthorNameProps) => {
  const { authorPageUrl, name } = byline
  if (renderLink) {
    return (
      <a
        href={authorPageUrl}
        className={`${componentClassName}__name`}
        data-link-category='article author'
        data-id={authorPageUrl}
        data-index={index}
        data-label={name}
      >
        {name}
        {showComma ? ',' : ''}
      </a>
    )
  } else {
    return (
      <span className={`${componentClassName}__name`}>
        {name}
        {showComma ? ',' : ''}
      </span>
    )
  }
}
